import React, { useState, useEffect, FormEvent, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@shootout-comps/core';
import ConfirmModal from '../components/ConfirmModal';
import PasswordInput from '../components/PasswordInput';
import { useLang } from '@shootout-comps/i18n';
import type { User } from '@shootout-comps/types';
import type { TournamentSummary } from '@shootout-comps/core';
import { useRequiredParams } from '../hooks/useRequiredParams';

type ActionPanel = { type: 'pw' | 'suspend'; userId: string } | null;

const STATUS_COLOURS: Record<string, string> = {
  active:               '#2ecc71',
  pending_verification: '#e67e22',
  suspended:            '#e74c3c',
  deactivated:          '#95a5a6',
};

const card: React.CSSProperties = { background: 'linear-gradient(135deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '12px', padding: '1.25rem 1.4rem', marginBottom: '1.25rem' };
const sectionLabel: React.CSSProperties = { fontSize: '0.75rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.85rem' };
const inp: React.CSSProperties = { padding: '0.55rem 0.75rem', background: 'rgba(0,0,0,0.3)', border: '1.5px solid rgba(201,168,76,0.25)', borderRadius: '7px', color: 'var(--chalk)', fontSize: '0.85rem', outline: 'none', minHeight: '40px' };
const goldBtn: React.CSSProperties = { background: 'linear-gradient(135deg,var(--gold),var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '7px', padding: '0.55rem 1.1rem', fontSize: '0.83rem', fontWeight: 700, cursor: 'pointer', minHeight: '40px' };
const sBtn: React.CSSProperties = { background: 'rgba(255,255,255,0.06)', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '5px', padding: '0.28rem 0.55rem', fontSize: '0.72rem', cursor: 'pointer', minHeight: '32px' };
const backBtn: React.CSSProperties = { background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '0.4rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1.25rem' };

function formatLastOnline(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins < 2)   return 'Just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7)   return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span style={{ fontSize: '0.62rem', padding: '0.15rem 0.45rem', borderRadius: '4px', background: `${STATUS_COLOURS[status] ?? '#888'}22`, color: STATUS_COLOURS[status] ?? '#888', border: `1px solid ${STATUS_COLOURS[status] ?? '#888'}44`, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
      {status.replace('_', ' ')}
    </span>
  );
}

function TournamentStatusBadge({ status }: { status: string }) {
  const colours: Record<string, string> = { registration: '#e67e22', active: '#2ecc71', complete: '#c9a84c' };
  const c = colours[status] ?? '#888';
  return (
    <span style={{ fontSize: '0.6rem', padding: '0.12rem 0.4rem', borderRadius: '4px', background: `${c}22`, color: c, border: `1px solid ${c}44`, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
      {status}
    </span>
  );
}

// ─── User Tournaments view ────────────────────────────────────────────────────

function UserTournamentsView({ user, onSelectTournament, onBack }: {
  user: User;
  onSelectTournament: (id: string) => void;
  onBack: () => void;
}) {
  const { t } = useLang();
  const [tournaments, setTournaments] = useState<TournamentSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem('ss_access_token');
      const res = await fetch(`/api/admin/users/${user._id}/tournaments`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await res.json() as TournamentSummary[] & { error?: string };
      if (!res.ok) throw new Error((data as unknown as { error?: string }).error ?? 'Failed');
      setTournaments(data as unknown as TournamentSummary[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  }, [user._id]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('ss_access_token');
      const res = await fetch(`/api/admin/tournaments/${id}`, {
        method: 'DELETE',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) {
        const d = await res.json() as { error?: string };
        throw new Error(d.error ?? 'Failed to delete');
      }
      setDeleteConfirm(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete tournament');
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: 'var(--page-pad)' }}>
      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)', padding: '1rem' }}>
          <div style={{ background: 'var(--baize)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: '12px', padding: '1.75rem', maxWidth: '380px', width: '100%', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🗑️</div>
            <h3 style={{ color: '#e74c3c', fontFamily: 'Bebas Neue', fontSize: '1.5rem', margin: '0 0 0.5rem' }}>{t.deleteTournamentTitle}</h3>
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', lineHeight: 1.6, marginBottom: '1.25rem' }}>
              {t.deleteTournamentConfirm.replace('{name}', deleteConfirm.name)}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                style={{ flex: 1, padding: '0.6rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '7px', color: 'var(--chalk-dim)', fontSize: '0.85rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={deleting}
                style={{ flex: 1, padding: '0.6rem', background: 'rgba(192,57,43,0.2)', border: '1px solid rgba(192,57,43,0.5)', borderRadius: '7px', color: '#e74c3c', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
              >
                {deleting ? 'Deleting…' : '🗑 Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <button onClick={onBack} style={backBtn}>{t.backToUsers}</button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'rgba(201,168,76,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
          {user.role === 'superuser' ? '👑' : '👤'}
        </div>
        <div>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: 'var(--gold)', lineHeight: 1, margin: 0 }}>
            {user.username}
          </h2>
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.78rem', margin: '0.2rem 0 0' }}>
            {user.email} · {t.tournamentsHeading}
          </p>
        </div>
      </div>

      {loading && <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem' }}>{t.loadingTournaments}</p>}
      {error && <p style={{ color: '#e74c3c', fontSize: '0.85rem' }}>{error}</p>}

      {tournaments !== null && tournaments.length === 0 && (
        <div style={{ ...card, textAlign: 'center', padding: '2.5rem' }}>
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.88rem', margin: 0 }}>
            {t.noTournamentsYet}
          </p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {tournaments?.map(tour => {
          const tourObj = tour as unknown as { _id: string; name: string; status: string; createdAt: string; players?: { isBye?: boolean }[] };
          const players = tourObj.players ?? [];
          const realPlayers = players.filter((p) => !p.isBye);
          const date = new Date(tourObj.createdAt).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric',
          });
          return (
            <div
              key={tourObj._id}
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
                border: '1px solid rgba(255,255,255,0.09)', borderRadius: '10px',
                padding: '1rem 1.2rem', transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: '0.75rem',
              }}
              className="tournament-row"
            >
              {/* Clickable info area */}
              <div
                onClick={() => onSelectTournament(tourObj._id)}
                style={{ flex: 1, cursor: 'pointer', minWidth: 0 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--chalk)' }}>{tourObj.name}</span>
                  <TournamentStatusBadge status={tourObj.status} />
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', marginTop: '0.25rem' }}>
                  📅 {date} · 👥 {realPlayers.length} player{realPlayers.length !== 1 ? 's' : ''}
                </div>
              </div>

              {/* View draw link */}
              <span
                onClick={() => onSelectTournament(tourObj._id)}
                style={{ color: 'var(--gold)', fontSize: '0.8rem', flexShrink: 0, cursor: 'pointer' }}
              >
                {t.viewDraw}
              </span>

              {/* Delete button — superuser only */}
              <button
                onClick={e => { e.stopPropagation(); setDeleteConfirm({ id: tourObj._id, name: tourObj.name }); }}
                title={t.deleteTournamentTitle}
                style={{ background: 'rgba(192,57,43,0.1)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: '6px', color: '#e74c3c', fontSize: '0.72rem', padding: '0.3rem 0.6rem', cursor: 'pointer', flexShrink: 0, minHeight: '32px' }}
              >
                🗑
              </button>
            </div>
          );
        })}
      </div>

      <style>{`.tournament-row:hover { border-color: rgba(201,168,76,0.3) !important; transform: translateY(-1px); box-shadow: 0 4px 16px rgba(0,0,0,0.3); }`}</style>
    </div>
  );
}

// ─── Admin tournament detail wrapper ─────────────────────────────────────────

function AdminTournamentView({ tournamentId, fromUser, matchId, onSelectMatch, onCloseMatch, onBack }: {
  tournamentId: string;
  fromUser: User;
  matchId: string | null;
  onSelectMatch: (id: string) => void;
  onCloseMatch: () => void;
  onBack: () => void;
}) {
  const { t } = useLang();
  const [tournament, setTournament] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true); setError(null);
    const token = localStorage.getItem('ss_access_token');
    fetch(`/api/admin/tournaments/${tournamentId}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
      .then(r => r.json())
      .then(data => {
        if ((data as { error?: string }).error) throw new Error((data as { error: string }).error);
        setTournament(data);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [tournamentId]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)', color: 'var(--chalk-dim)' }}>
      <p>{t.loadingTournament}</p>
    </div>
  );

  if (error || !tournament) return (
    <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '0 1rem' }}>
      <button onClick={onBack} style={backBtn}>← {fromUser.username} {t.tournamentsHeading}</button>
      <p style={{ color: '#e74c3c' }}>{error ?? t.tournamentNotFound}</p>
    </div>
  );

  // Inject a custom fetchHistoricTournament that uses the admin endpoint by temporarily
  // rendering HistoricTournamentPage with the admin-fetched data pre-loaded via a key trick.
  // Simplest approach: just re-use HistoricTournamentPage but override the fetch in context.
  // Since we can't easily override context, render a self-contained view using the fetched data.
  return (
    <AdminTournamentDetail
      tournament={tournament}
      onBack={onBack}
      fromUser={fromUser}
      matchId={matchId}
      onSelectMatch={onSelectMatch}
      onCloseMatch={onCloseMatch}
    />
  );
}

// Self-contained tournament detail that doesn't require TournamentContext ownership check
function AdminTournamentDetail({ tournament, onBack, fromUser, matchId, onSelectMatch, onCloseMatch }: {
  tournament: unknown;
  onBack: () => void;
  fromUser: User;
  matchId: string | null;
  onSelectMatch: (id: string) => void;
  onCloseMatch: () => void;
}) {
  const { t } = useLang();
  const td = tournament as {
    _id: string; name: string; status: string; createdAt: string;
    players: { _id: string; name: string; seed: number; isBye?: boolean; profilePictureUrl?: string }[];
    rounds: { round: number; name: string; matches: { _id: string; player1: unknown; player2: unknown; winner: unknown; score1: number | null; score2: number | null; status: string }[] }[];
  };

  const realPlayers = td.players.filter(p => !p.isBye);
  const champion = td.status === 'complete'
    ? (td.rounds[td.rounds.length - 1]?.matches[0]?.winner as { name: string; profilePictureUrl?: string } | null)
    : null;
  const createdAt = new Date(td.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const totalRounds = Math.log2((td.rounds[0]?.matches.length ?? 1)) + 1;
  function getRoundName(totalR: number, roundIdx: number): string {
    const fromEnd = totalR - 1 - roundIdx;
    if (fromEnd === 0) return t.roundFinal;
    if (fromEnd === 1) return t.roundSemiFinals;
    if (fromEnd === 2) return t.roundQuarterFinals;
    if (fromEnd === 3) return t.roundOf16;
    return `${t.roundOf} ${Math.pow(2, fromEnd + 1)}`;
  }

  const PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"%3E%3Ccircle cx="32" cy="32" r="32" fill="%23236038"/%3E%3Ccircle cx="32" cy="24" r="10" fill="%23c9a84c" opacity="0.7"/%3E%3Cellipse cx="32" cy="52" rx="16" ry="10" fill="%23c9a84c" opacity="0.7"/%3E%3C/svg%3E';

  if (matchId) {
    // Fetch and show match detail
    return <AdminMatchDetail tournamentId={td._id} matchId={matchId} onBack={onCloseMatch} />;
  }

  return (
    <div style={{ padding: 'var(--page-pad)' }}>
      <button onClick={onBack} style={backBtn}>← {fromUser.username} {t.tournamentsHeading}</button>

      {/* Tournament header */}
      <div style={{ maxWidth: '900px', margin: '0 auto 1.5rem', background: 'linear-gradient(135deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '2.2rem', color: 'var(--gold)', lineHeight: 1, margin: 0 }}>{td.name}</h2>
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginTop: '0.3rem' }}>
              📅 {createdAt} · 👥 {realPlayers.length} players · Owner: <strong style={{ color: 'var(--chalk)' }}>{fromUser.username}</strong>
            </p>
          </div>
          {champion && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem' }}>🏆</div>
              <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Champion</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.35rem' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', border: '2px solid rgba(201,168,76,0.3)', background: '#1a4a2e', flexShrink: 0 }}>
                  <img src={champion.profilePictureUrl || PLACEHOLDER} alt={champion.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <span style={{ fontWeight: 700, color: 'var(--gold)', fontSize: '0.95rem' }}>{champion.name}</span>
              </div>
            </div>
          )}
        </div>

        {realPlayers.length > 0 && (
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '1rem' }}>
            <div style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.6rem' }}>Participants</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {realPlayers.sort((a, b) => a.seed - b.seed).map(p => (
                <div key={p._id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '20px', padding: '0.25rem 0.6rem 0.25rem 0.25rem', fontSize: '0.78rem' }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', overflow: 'hidden', border: '2px solid rgba(201,168,76,0.3)', background: '#1a4a2e', flexShrink: 0 }}>
                    <img src={p.profilePictureUrl || PLACEHOLDER} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <span>{p.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bracket */}
      {t.rounds.length > 0 && (
        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: '1rem', padding: '0 var(--page-pad) 1rem', alignItems: 'flex-start', minWidth: 'max-content', margin: '0 auto' }}>
            {td.rounds.map((round, roundIdx) => {
              const done = round.matches.every(m => m.status === 'complete');
              const roundName = getRoundName(totalRounds, roundIdx);
              return (
                <div key={round.round} style={{ minWidth: '200px', maxWidth: '220px', flexShrink: 0 }}>
                  <div style={{ textAlign: 'center', marginBottom: '0.85rem', paddingBottom: '0.6rem', borderBottom: `2px solid ${done ? 'var(--gold)' : 'rgba(255,255,255,0.1)'}` }}>
                    <h3 style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: done ? 'var(--gold)' : 'var(--chalk)', letterSpacing: '0.04em', margin: 0 }}>{roundName}</h3>
                    <span style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)' }}>
                      {round.matches.filter(m => m.status === 'complete').length}/{round.matches.length} played
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    {round.matches.map(match => {
                      const p1 = match.player1 as { _id: string; name: string; profilePictureUrl?: string };
                      const p2 = match.player2 as { _id: string; name: string; profilePictureUrl?: string; isBye?: boolean } | null;
                      const winner = match.winner as { _id: string } | null;
                      const complete = match.status === 'complete';
                      const isBye = p2?.isBye;
                      const clickable = complete && !isBye;
                      return (
                        <div key={match._id}
                          onClick={() => clickable && onSelectMatch(match._id)}
                          style={{ background: complete ? 'linear-gradient(135deg,rgba(201,168,76,0.07),rgba(255,255,255,0.02))' : 'rgba(255,255,255,0.03)', border: `1px solid ${complete ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.06)'}`, borderRadius: '8px', padding: '0.55rem', cursor: clickable ? 'pointer' : 'default', transition: 'all 0.15s' }}
                          className={clickable ? 'hist-card' : ''}
                        >
                          {/* Player 1 */}
                          {(() => {
                            const isWinner = winner?._id === p1._id;
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.4rem', background: isWinner ? 'rgba(201,168,76,0.1)' : 'transparent', borderRadius: '4px', opacity: complete && !isWinner ? 0.45 : 1 }}>
                                <div style={{ width: 22, height: 22, borderRadius: '50%', overflow: 'hidden', border: '1px solid rgba(201,168,76,0.3)', background: '#1a4a2e', flexShrink: 0 }}>
                                  <img src={p1.profilePictureUrl || PLACEHOLDER} alt={p1.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                                <span style={{ flex: 1, fontSize: '0.8rem', fontWeight: isWinner ? 600 : 400, color: isWinner ? 'var(--gold)' : 'var(--chalk)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p1.name}</span>
                                {complete && match.score1 !== null && <span style={{ fontFamily: 'Bebas Neue', fontSize: '1rem', color: isWinner ? 'var(--gold)' : 'var(--chalk-dim)', minWidth: '18px', textAlign: 'right' }}>{match.score1}</span>}
                                {isWinner && <span style={{ fontSize: '0.58rem' }}>✓</span>}
                              </div>
                            );
                          })()}
                          {/* Divider */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.1rem 0.4rem' }}>
                            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
                            <span style={{ fontSize: '0.55rem', color: 'var(--chalk-dim)' }}>VS</span>
                            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
                          </div>
                          {/* Player 2 */}
                          {p2 ? (() => {
                            const isWinner = winner?._id === p2._id;
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.4rem', background: isWinner ? 'rgba(201,168,76,0.1)' : 'transparent', borderRadius: '4px', opacity: complete && !isWinner ? 0.45 : 1 }}>
                                <div style={{ width: 22, height: 22, borderRadius: '50%', overflow: 'hidden', border: '1px solid rgba(201,168,76,0.3)', background: '#1a4a2e', flexShrink: 0 }}>
                                  <img src={p2.profilePictureUrl || PLACEHOLDER} alt={p2.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                                <span style={{ flex: 1, fontSize: '0.8rem', fontWeight: isWinner ? 600 : 400, color: isWinner ? 'var(--gold)' : 'var(--chalk)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p2.name}</span>
                                {complete && match.score2 !== null && <span style={{ fontFamily: 'Bebas Neue', fontSize: '1rem', color: isWinner ? 'var(--gold)' : 'var(--chalk-dim)', minWidth: '18px', textAlign: 'right' }}>{match.score2}</span>}
                                {isWinner && <span style={{ fontSize: '0.58rem' }}>✓</span>}
                              </div>
                            );
                          })() : <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', padding: '0.3rem 0.5rem', fontStyle: 'italic' }}>BYE</div>}
                          {clickable && <div style={{ textAlign: 'center', marginTop: '0.35rem' }}><span style={{ fontSize: '0.6rem', color: 'var(--gold)' }}>→ View details</span></div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <style>{`.hist-card:hover{transform:translateY(-1px);box-shadow:0 4px 14px rgba(0,0,0,0.3)}`}</style>
    </div>
  );
}

function AdminMatchDetail({ tournamentId, matchId, onBack }: { tournamentId: string; matchId: string; onBack: () => void }) {
  const { t } = useLang();
  const [match, setMatch] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('ss_access_token');
    fetch(`/api/admin/tournaments/${tournamentId}/matches/${matchId}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
      .then(r => r.json())
      .then(setMatch)
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [tournamentId, matchId]);

  if (loading) return <div style={{ padding: 'var(--page-pad)', color: 'var(--chalk-dim)' }}>{t.loadingMatch}</div>;

  // Delegate to HistoricTournamentPage's internal MatchDetailView via a workaround:
  // We can't import MatchDetailView (it's not exported). Instead render inline.
  // For simplicity, show basic score info — full shot history requires exporting MatchDetailView.
  const m = match as { player1: { name: string }; player2: { name: string } | null; score1: number | null; score2: number | null; winner: { _id: string; name: string } | null; liveState?: { shots: unknown[]; elapsedSeconds: number } } | null;
  if (!m) return <div style={{ padding: 'var(--page-pad)' }}><button onClick={onBack} style={backBtn}>← Back</button><p style={{ color: '#e74c3c' }}>{t.matchNotFound}</p></div>;

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto', padding: 'var(--page-pad)' }}>
      <button onClick={onBack} style={backBtn}>{t.backToDraw}</button>
      <div style={{ background: 'linear-gradient(135deg,rgba(201,168,76,0.08),rgba(255,255,255,0.02))', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '14px', padding: '1.5rem', marginTop: '0.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', gap: '1rem' }}>
          {[{ p: m.player1, score: m.score1 }, { p: m.player2, score: m.score2 }].filter(r => r.p).map((row, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 600, fontSize: '1rem', color: m.winner?.name === row.p?.name ? 'var(--gold)' : 'var(--chalk)' }}>{row.p?.name}</div>
              {m.winner?.name === row.p?.name && <div style={{ fontSize: '0.65rem', color: 'var(--gold)' }}>🏆 Winner</div>}
              <div style={{ fontFamily: 'Bebas Neue', fontSize: '3rem', color: m.winner?.name === row.p?.name ? 'var(--gold)' : 'var(--chalk)', lineHeight: 1 }}>{row.score ?? '—'}</div>
            </div>
          ))}
        </div>
        {m.liveState && (
          <div style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.8rem', color: 'var(--chalk-dim)' }}>
            ⏱ {Math.floor((m.liveState.elapsedSeconds ?? 0) / 60)}:{String((m.liveState.elapsedSeconds ?? 0) % 60).padStart(2, '0')} played · {m.liveState.shots.length} shots
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Route adapters ─────────────────────────────────────────────────────────
// Mounted at `/admin/users/*` (see App.tsx). `user`/`fromUser` are resolved from the already-
// loaded `users` list by id rather than refetched — same derivation-not-refetch pattern used
// throughout this router migration.

function UserTournamentsRoute({ users }: { users: User[] }) {
  const params = useRequiredParams<{ userId: string }>(['userId'], '/admin/users');
  const navigate = useNavigate();
  if (!params) return null;
  const user = users.find(u => u._id === params.userId);
  if (!user) return <Navigate to="/admin/users" replace />;
  return (
    <UserTournamentsView
      user={user}
      onSelectTournament={id => navigate(`/admin/users/${params.userId}/tournaments/${id}`)}
      onBack={() => navigate('/admin/users')}
    />
  );
}

function AdminTournamentRoute({ users }: { users: User[] }) {
  const params = useRequiredParams<{ userId: string; tournamentId: string }>(['userId', 'tournamentId'], '/admin/users');
  const { matchId } = useParams<{ matchId?: string }>();
  const navigate = useNavigate();
  if (!params) return null;
  const fromUser = users.find(u => u._id === params.userId);
  if (!fromUser) return <Navigate to="/admin/users" replace />;
  return (
    <AdminTournamentView
      tournamentId={params.tournamentId}
      fromUser={fromUser}
      matchId={matchId ?? null}
      onSelectMatch={id => navigate(`/admin/users/${params.userId}/tournaments/${params.tournamentId}/match/${id}`)}
      onCloseMatch={() => navigate(`/admin/users/${params.userId}/tournaments/${params.tournamentId}`)}
      onBack={() => navigate(`/admin/users/${params.userId}`)}
    />
  );
}

// ─── Main UsersPage ───────────────────────────────────────────────────────────

export default function UsersPage() {
  const { user: me, listUsers, createUser, deleteUser, changeUserPassword } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [users, setUsers]         = useState<User[] | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [newUsername, setNu]      = useState('');
  const [newEmail, setNe]         = useState('');
  const [newPassword, setNp]      = useState('');
  const [creating, setCreating]   = useState(false);
  const [createError, setCe]      = useState('');
  const [panel, setPanel]         = useState<ActionPanel>(null);
  const [panelVal, setPanelVal]   = useState('');
  const [panelBusy, setPanelBusy] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

  const showErr = (msg: string) => { setError(msg); setTimeout(() => setError(null), 4000); };

  const reload = async () => {
    setLoading(true);
    try { setUsers(await listUsers()); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line

  // ── Users list view ────────────────────────────────────────────────────────
  const handleCreate = async (e: FormEvent) => {
    e.preventDefault(); setCreating(true); setCe('');
    try { await createUser({ username: newUsername.trim(), email: newEmail.trim(), password: newPassword }); setNu(''); setNe(''); setNp(''); await reload(); }
    catch (e) { setCe(e instanceof Error ? e.message : 'Failed'); }
    finally { setCreating(false); }
  };

  const authedPut = async (path: string, body?: object): Promise<User> => {
    const token = localStorage.getItem('ss_access_token');
    const res = await fetch(`/api/auth${path}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body ?? {}) });
    const data = await res.json() as User & { error?: string };
    if (!res.ok) throw new Error(data.error ?? 'Failed');
    return data;
  };

  const handleSuspend = async (userId: string) => {
    setPanelBusy(true);
    try { await authedPut(`/users/${userId}/suspend`, { reason: panelVal }); setPanel(null); setPanelVal(''); await reload(); }
    catch (e) { showErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setPanelBusy(false); }
  };

  const handleActivate = async (userId: string) => {
    try { await authedPut(`/users/${userId}/activate`); await reload(); }
    catch (e) { showErr(e instanceof Error ? e.message : 'Failed'); }
  };

  const handleDeactivate = (userId: string) => {
    setConfirmModal({
      title: t.deactivateAccount,
      message: 'Deactivate this account? The user will not be able to sign in.',
      onConfirm: async () => {
        setConfirmModal(null);
        try { await authedPut(`/users/${userId}/deactivate`); await reload(); }
        catch (e) { showErr(e instanceof Error ? e.message : 'Failed'); }
      },
    });
  };

  const handlePwChange = async (userId: string) => {
    if (panelVal.length < 8) { showErr(t.passwordMinLength); return; }
    setPanelBusy(true);
    try { await changeUserPassword(userId, panelVal); setPanel(null); setPanelVal(''); showErr(t.passwordUpdated); }
    catch (e) { showErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setPanelBusy(false); }
  };

  const handleToggleFreeForLife = async (userId: string, enabled: boolean) => {
    try { await authedPut(`/users/${userId}/free-tournaments`, { enabled }); await reload(); }
    catch (e) { showErr(e instanceof Error ? e.message : 'Failed'); }
  };

  const handleDelete = (id: string, username: string) => {
    setConfirmModal({
      title: 'Delete User',
      message: `Delete user "${username}"? Their tournaments are preserved but this cannot be undone.`,
      onConfirm: async () => {
        setConfirmModal(null);
        try { await deleteUser(id); await reload(); }
        catch (e) { showErr(e instanceof Error ? e.message : 'Failed'); }
      },
    });
  };

  const usersListView = (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: 'var(--page-pad)' }}>
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={t.confirmLabel}
          cancelLabel={t.cancelLabel}
          danger
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      <h2 style={{ fontSize: '2.2rem', color: 'var(--gold)', marginBottom: '1.75rem' }}>{t.userManagement}</h2>

      {/* Create form */}
      <div style={card}>
        <h3 style={sectionLabel}>{t.addNewUser}</h3>
        <form onSubmit={handleCreate} style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <input value={newUsername} onChange={e => setNu(e.target.value)} placeholder={t.username} style={{ ...inp, flex: '1 1 120px' }} />
          <input type="email" value={newEmail} onChange={e => setNe(e.target.value)} placeholder="Email" style={{ ...inp, flex: '1 1 160px' }} />
          <PasswordInput value={newPassword} onChange={e => setNp(e.target.value)} placeholder={t.password} style={inp} containerStyle={{ flex: '1 1 140px' }} />
          <button type="submit" disabled={creating || !newUsername || !newEmail || !newPassword} style={goldBtn}>{creating ? t.creating : t.createUser}</button>
        </form>
        {createError && <p style={{ color: '#e74c3c', fontSize: '0.8rem', marginTop: '0.5rem' }}>{createError}</p>}
      </div>

      {/* Users list */}
      <div style={card}>
        <h3 style={sectionLabel}>{t.allUsers} ({users?.length ?? 0})</h3>
        {loading && <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem' }}>{t.loadingLabel}</p>}
        {error && <p style={{ color: '#e74c3c', fontSize: '0.85rem' }}>{error}</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {users?.map(u => (
            <div key={u._id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: u._id === me?._id ? 'rgba(201,168,76,0.06)' : 'rgba(255,255,255,0.03)', border: `1px solid ${u._id === me?._id ? 'rgba(201,168,76,0.18)' : 'rgba(255,255,255,0.07)'}`, borderRadius: '8px', padding: '0.65rem 0.85rem', flexWrap: 'wrap' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: u.role === 'superuser' ? 'rgba(201,168,76,0.18)' : 'rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', flexShrink: 0 }}>
                  {u.role === 'superuser' ? '👑' : '👤'}
                </div>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{u.username}</span>
                    {u._id === me?._id && <span style={{ fontSize: '0.6rem', color: 'var(--gold)', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '4px', padding: '0.1rem 0.35rem' }}>{t.you}</span>}
                    <StatusBadge status={u.status} />
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', marginTop: '0.1rem' }}>
                    {u.email} · {t.joined} {new Date(u.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {' · '}🕐 {formatLastOnline((u as unknown as { lastOnline?: string | null }).lastOnline)}
                  </div>
                  {u.suspendedReason && <div style={{ fontSize: '0.7rem', color: '#e74c3c', marginTop: '0.1rem' }}>Reason: {u.suspendedReason}</div>}
                </div>
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* Tournaments drill-down */}
                  <button
                    onClick={() => navigate(`/admin/users/${u._id}`)}
                    style={{ ...sBtn, color: 'var(--gold)', borderColor: 'rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.06)' }}
                    title={t.viewThisUsersTournaments}
                  >
                    🏆 {t.tournamentsHeading}
                  </button>
                  {u.role !== 'superuser' && (
                    <>
                      {(u.status === 'suspended' || u.status === 'deactivated') && (
                        <button onClick={() => handleActivate(u._id)} style={{ ...sBtn, color: '#2ecc71', borderColor: 'rgba(46,204,113,0.3)', background: 'rgba(46,204,113,0.08)' }}>✓ Activate</button>
                      )}
                      {u.status === 'active' && (
                        <button onClick={() => { setPanel({ type: 'suspend', userId: u._id }); setPanelVal(''); }} style={{ ...sBtn, color: '#e67e22', borderColor: 'rgba(230,126,34,0.3)', background: 'rgba(230,126,34,0.08)' }}>⏸ Suspend</button>
                      )}
                      {u.status !== 'deactivated' && (
                        <button onClick={() => handleDeactivate(u._id)} style={{ ...sBtn, color: '#95a5a6', borderColor: 'rgba(149,165,166,0.25)' }}>🚫 Deactivate</button>
                      )}
                      <button onClick={() => { setPanel({ type: 'pw', userId: u._id }); setPanelVal(''); }} style={sBtn}>{t.resetPasswordBtn}</button>
                      <button
                        onClick={() => handleToggleFreeForLife(u._id, !(u as unknown as { freeTournamentsForLife?: boolean }).freeTournamentsForLife)}
                        style={{ ...sBtn, color: (u as unknown as { freeTournamentsForLife?: boolean }).freeTournamentsForLife ? '#f1c40f' : 'var(--chalk-dim)', borderColor: (u as unknown as { freeTournamentsForLife?: boolean }).freeTournamentsForLife ? 'rgba(241,196,15,0.4)' : 'rgba(255,255,255,0.1)', background: (u as unknown as { freeTournamentsForLife?: boolean }).freeTournamentsForLife ? 'rgba(241,196,15,0.1)' : 'transparent' }}
                        title={(u as unknown as { freeTournamentsForLife?: boolean }).freeTournamentsForLife ? t.revokeFreeForLife : t.grantFreeForLife}
                      >
                        {(u as unknown as { freeTournamentsForLife?: boolean }).freeTournamentsForLife ? t.freeForLifeGranted : t.freeForLife}
                      </button>
                      <button onClick={() => handleDelete(u._id, u.username)} style={{ ...sBtn, color: '#e74c3c', borderColor: 'rgba(192,57,43,0.25)', background: 'rgba(192,57,43,0.06)' }}>{t.deleteUser}</button>
                    </>
                  )}
                </div>
              </div>
              {panel?.userId === u._id && (
                <div style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '0 0 8px 8px', padding: '0.75rem 0.85rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {panel.type === 'pw' ? (
                    <PasswordInput value={panelVal} onChange={e => setPanelVal(e.target.value)} placeholder="New password (min 8)" style={{ ...inp, fontSize: '0.82rem', padding: '0.45rem 0.7rem' }} containerStyle={{ flex: '1 1 180px' }} />
                  ) : (
                    <input value={panelVal} onChange={e => setPanelVal(e.target.value)} type="text" placeholder="Suspension reason (optional)" style={{ ...inp, flex: '1 1 180px', fontSize: '0.82rem', padding: '0.45rem 0.7rem' }} />
                  )}
                  <button disabled={panelBusy} onClick={() => panel.type === 'pw' ? handlePwChange(u._id) : handleSuspend(u._id)} style={{ ...goldBtn, padding: '0.45rem 1rem', fontSize: '0.8rem', minHeight: '38px' }}>
                    {panelBusy ? '…' : panel.type === 'pw' ? 'Save' : t.confirmSuspend}
                  </button>
                  <button onClick={() => { setPanel(null); setPanelVal(''); }} style={{ ...sBtn, minHeight: '38px' }}>✕</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...card, borderColor: 'rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.02)' }}>
        <p style={{ fontSize: '0.78rem', color: 'var(--chalk-dim)', lineHeight: 1.6, margin: 0 }}>
          <strong style={{ color: 'var(--chalk)' }}>{t.usersAccountStates}</strong>{' '}
          <span style={{ color: STATUS_COLOURS.active }}>{t.usersActiveState}</span> — full access ·{' '}
          <span style={{ color: STATUS_COLOURS.pending_verification }}>{t.usersPendingState}</span> — awaiting email verification ·{' '}
          <span style={{ color: STATUS_COLOURS.suspended }}>{t.usersSuspendedState}</span> — cannot sign in ·{' '}
          <span style={{ color: STATUS_COLOURS.deactivated }}>{t.usersDeactivatedState}</span> — permanently disabled
        </p>
      </div>
    </div>
  );

  return (
    <Routes>
      <Route index element={usersListView} />
      <Route path=":userId" element={<UserTournamentsRoute users={users ?? []} />} />
      <Route path=":userId/tournaments/:tournamentId" element={<AdminTournamentRoute users={users ?? []} />} />
      <Route path=":userId/tournaments/:tournamentId/match/:matchId" element={<AdminTournamentRoute users={users ?? []} />} />
      <Route path="*" element={<Navigate to="/admin/users" replace />} />
    </Routes>
  );
}
